/**
 * Crash forensics (M33). A Home Screen app that iOS closes in the foreground — out of memory (WebGL targets, decoded
 * images and audio count against the page), or its GPU process reset — comes back on the launch screen with no word
 * of why: Kevin's "the game keeps resetting back to the launch screen when I finish some of the levels".
 *
 * So the game leaves a small breadcrumb in localStorage — what was on screen, the level and its stage and tick, the
 * last frame-rate window, the renderer's memory counts, the live WebGL contexts, the last context loss — rewritten
 * every `EveryMs` and whenever the screen changes, and marked when the page is hidden or closed the ordinary way. The
 * next boot reads it: a breadcrumb still marked live, from a load that was not one of the game's own reloads
 * (infra/reloadNote.ts), is a page iOS closed under the player. That is recorded for Settings → Copy diagnostics and
 * answered with a calm toast; one marked hidden is a backgrounded app iOS reclaimed (normal, recorded, no toast).
 *
 * Pure bookkeeping over an injected store and clock, so it is unit-tested in Node.
 */
import type { NoteStore } from "./reloadNote";

export type CrumbState = "live" | "hidden" | "closed";

/** What was going on, as of `at`. */
export interface CrumbFacts {
  /** title · hub · map · level · end · loading */
  readonly screen: string;
  readonly level: number | null;
  readonly stage: number | null;
  readonly tick: number | null;
  /** The last frame-rate window (frameJournal), as its diagnostics line. */
  readonly frames: string | null;
  /** The main renderer's live geometries and textures, and the last frame's draw calls. */
  readonly gpu: { readonly geoms: number; readonly texs: number; readonly calls: number } | null;
  /** The WebGL contexts alive (infra/glContexts.ts). */
  readonly contexts: readonly string[];
  /** A battle's suspended copy was in the save. */
  readonly battleSaved: boolean;
}

export interface Crumb extends CrumbFacts {
  readonly v: 1;
  readonly build: string;
  readonly startedAt: number;
  readonly at: number;
  readonly state: CrumbState;
  /** The last WebGL context loss of this page ("main at L6 end", with its time), or null. */
  readonly lastLoss: string | null;
}

export type CrashKind = "foreground" | "background";
export interface CrashRecord { readonly kind: CrashKind; readonly detectedAt: number; readonly crumb: Crumb }

export const CrumbKey = "highmarch.crumb";
export const CrashesKey = "highmarch.crashes";

const EMPTY: CrumbFacts = { screen: "boot", level: null, stage: null, tick: null, frames: null, gpu: null, contexts: [], battleSaved: false };

export class Breadcrumbs {
  /** How often the breadcrumb is rewritten while the page is up (ms). */
  static readonly EveryMs = 10_000;
  /** Crash records kept for diagnostics. */
  static readonly Kept = 5;

  private facts: CrumbFacts = EMPTY;
  private state: CrumbState = "live";
  private lastLoss: string | null = null;
  private lastWrite = -Infinity;
  private readonly startedAt: number;

  constructor(private readonly store: NoteStore | null, private readonly build: string, private readonly clock: () => number = Date.now) {
    this.startedAt = clock();
  }

  /**
   * Boot: how the last session ended. `ownReload` is this load being one of the game's own reloads (its reload note
   * was found). Returns the crash it recorded, or null for a clean end, a first run or our own reload. Then starts
   * this session's breadcrumb.
   */
  boot(ownReload: boolean): CrashRecord | null {
    const prev = this.read();
    let crash: CrashRecord | null = null;
    if (prev && prev.state !== "closed" && !ownReload) {
      crash = { kind: prev.state === "live" ? "foreground" : "background", detectedAt: this.clock(), crumb: prev };
      const list = [...this.crashes(), crash].slice(-Breadcrumbs.Kept);
      this.put(CrashesKey, JSON.stringify(list));
    }
    this.state = "live";
    this.write();
    return crash;
  }

  /** The crashes recorded so far, oldest first. */
  crashes(): CrashRecord[] {
    const raw = this.get(CrashesKey);
    if (!raw) return [];
    try {
      const list = JSON.parse(raw) as unknown;
      return Array.isArray(list) ? (list as CrashRecord[]).filter((c) => c && typeof c === "object" && c.crumb && typeof c.crumb.at === "number") : [];
    } catch { return []; }
  }

  /** The breadcrumb as last written, or null. */
  read(): Crumb | null {
    const raw = this.get(CrumbKey);
    if (!raw) return null;
    try {
      const c = JSON.parse(raw) as Partial<Crumb>;
      return c && c.v === 1 && typeof c.at === "number" && (c.state === "live" || c.state === "hidden" || c.state === "closed") ? (c as Crumb) : null;
    } catch { return null; }
  }

  /**
   * The current facts (the game calls this every second or so): written when the screen or level changed, or
   * `EveryMs` after the last write. Returns whether it wrote.
   */
  update(facts: CrumbFacts): boolean {
    const moved = facts.screen !== this.facts.screen || facts.level !== this.facts.level || facts.stage !== this.facts.stage || facts.battleSaved !== this.facts.battleSaved;
    this.facts = facts;
    if (!moved && this.clock() - this.lastWrite < Breadcrumbs.EveryMs) return false;
    this.write();
    return true;
  }

  /** A WebGL context was lost (written at once: a GPU process reset is often what comes just before the end). */
  loss(what: string): void {
    this.lastLoss = `${what} at ${this.facts.screen}${this.facts.level !== null ? ` L${this.facts.level}` : ""} · ${new Date(this.clock()).toISOString().slice(11, 19)}`;
    this.write();
  }

  /** The page went out of sight (true) or came back (false). */
  visibility(hidden: boolean): void { this.state = hidden ? "hidden" : "live"; this.write(); }

  /** The page is being unloaded the ordinary way (pagehide: a reload, a navigation, a close the browser reports). */
  closed(): void { this.state = "closed"; this.write(); }

  /** The breadcrumb this session would write now (for Copy diagnostics). */
  current(): Crumb {
    return { v: 1, build: this.build, startedAt: this.startedAt, at: this.clock(), state: this.state, lastLoss: this.lastLoss, ...this.facts };
  }

  private write(): void {
    this.lastWrite = this.clock();
    this.put(CrumbKey, JSON.stringify(this.current()));
  }

  private get(k: string): string | null { try { return this.store?.getItem(k) ?? null; } catch { return null; } }
  private put(k: string, v: string): void { try { this.store?.setItem(k, v); } catch { /* private mode or full: forensics are best effort */ } }
}

/** One crash as Copy diagnostics prints it. */
export function crashLines(c: CrashRecord): string[] {
  const b = c.crumb, t = (ms: number) => new Date(ms).toISOString().replace("T", " ").slice(0, 19);
  const where = `${b.screen}${b.level !== null ? ` L${b.level}` : ""}${b.stage !== null ? ` stage ${b.stage}` : ""}${b.tick !== null ? ` tick ${b.tick}` : ""}`;
  return [
    `  ${t(b.at)} UTC · ${c.kind === "foreground" ? "closed by iOS while playing" : "reclaimed by iOS in the background"} · last seen on ${where}`,
    `    build ${b.build} · up ${Math.round((b.at - b.startedAt) / 1000)} s · contexts ${b.contexts.length > 0 ? b.contexts.join(", ") : "none"}${b.gpu ? ` · geoms ${b.gpu.geoms} texs ${b.gpu.texs} draws ${b.gpu.calls}` : ""}${b.battleSaved ? " · battle saved" : ""}`,
    ...(b.frames ? [`    frames: ${b.frames.trim()}`] : []),
    ...(b.lastLoss ? [`    last context loss: ${b.lastLoss}`] : []),
  ];
}

/** What the game says after a crash it found at boot (null: nothing to say — a background reclaim is ordinary). */
export function crashToastText(c: CrashRecord | null, battleSaved: boolean): string | null {
  if (!c || c.kind !== "foreground") return null;
  return battleSaved ? "iOS closed the game. Your battle waits where you left it" : "iOS closed the game. Your progress is safe";
}

function localStore(): NoteStore | null {
  try { return typeof localStorage !== "undefined" ? localStorage : null; } catch { return null; }
}

/** The page's breadcrumbs (main.ts boots and feeds them; the scene rig reports context losses). */
export const breadcrumbs = new Breadcrumbs(localStore(), typeof __BUILD_STAMP__ === "string" ? __BUILD_STAMP__ : "dev");
